import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { SortDirection } from '@quds/shared';

/** Scroll container: wide tables scroll horizontally on narrow screens instead of collapsing. */
export function TableContainer({ children, className, label }: { children: ReactNode; className?: string; label?: string }) {
  return (
    <div role="region" aria-label={label} tabIndex={label ? 0 : undefined} className={cn('relative overflow-x-auto focus-visible:shadow-focus', className)}>
      {children}
    </div>
  );
}

export function Table({ className, ...rest }: HTMLAttributes<HTMLTableElement>) {
  return <table className={cn('w-full border-collapse text-left text-sm', className)} {...rest} />;
}

export function THead({ className, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn('bg-surface-muted', className)} {...rest} />;
}

export function TBody({ className, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn('divide-y divide-line', className)} {...rest} />;
}

export function TR({ className, interactive = false, ...rest }: HTMLAttributes<HTMLTableRowElement> & { interactive?: boolean }) {
  return <tr className={cn(interactive && 'transition-colors hover:bg-primary-soft/40', className)} {...rest} />;
}

interface THProps extends ThHTMLAttributes<HTMLTableCellElement> {
  sortable?: boolean;
  sortDirection?: SortDirection | null;
  onSort?: () => void;
  align?: 'left' | 'right' | 'center';
}

export function TH({ sortable, sortDirection, onSort, align = 'left', className, children, ...rest }: THProps) {
  const alignment = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  const ariaSort = sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : sortable ? 'none' : undefined;
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={cn(
        'h-10 whitespace-nowrap border-b border-line px-4 text-xs font-semibold uppercase tracking-wide text-ink-secondary',
        alignment,
        className,
      )}
      {...rest}
    >
      {sortable ? (
        <button
          type="button"
          onClick={onSort}
          className={cn(
            '-mx-1.5 inline-flex items-center gap-1 rounded px-1.5 py-1 uppercase tracking-wide transition-colors hover:bg-surface-sunken hover:text-ink focus-visible:shadow-focus focus-visible:outline-none',
            sortDirection && 'text-ink',
          )}
        >
          {children}
          {sortDirection === 'asc' ? (
            <ArrowUp size={13} aria-hidden="true" />
          ) : sortDirection === 'desc' ? (
            <ArrowDown size={13} aria-hidden="true" />
          ) : (
            <ChevronsUpDown size={13} aria-hidden="true" className="text-ink-muted" />
          )}
        </button>
      ) : (
        children
      )}
    </th>
  );
}

export function TD({ className, align = 'left', ...rest }: TdHTMLAttributes<HTMLTableCellElement> & { align?: 'left' | 'right' | 'center' }) {
  const alignment = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return <td className={cn('h-14 whitespace-nowrap px-4 align-middle text-ink', alignment, className)} {...rest} />;
}
